import { AllocationInterval, Holiday } from '../types'
import { dateCalculationService } from './dateCalculationService'

// Empty set used as default when no time-off context is needed
const EMPTY_TIME_OFF = new Set<string>()

interface DailyHoursMap {
  [date: string]: number
}

class AllocationDistributionService {
  private parseDateOnly(dateStr: string): Date {
    const clean = typeof dateStr === 'string' ? dateStr.substring(0, 10) : String(dateStr)
    const [year, month, day] = clean.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day))
  }

  private toDateOnlyString(date: Date): string {
    return date.toISOString().substring(0, 10)
  }

  private getWeekKey(date: Date): string {
    const year = date.getUTCFullYear()
    const weekNum = dateCalculationService.getWeekNumber(date)
    return `${year}-W${weekNum.toString().padStart(2, '0')}`
  }

  private getMonthKey(date: Date): string {
    const year = date.getUTCFullYear()
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    return `${year}-${month}`
  }

  private isAvailableDay(date: Date, holidays: Holiday[], timeOffDates: Set<string>): boolean {
    return dateCalculationService.isAvailableDay(date, holidays, timeOffDates)
  }

  private getWorkingDaysInWeek(weekStart: Date, allocationStart: Date, allocationEnd: Date, holidays: Holiday[], timeOffDates: Set<string>): string[] {
    const days: string[] = []
    const weekEnd = new Date(weekStart.getTime())
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 6)

    const cur = new Date(weekStart.getTime())
    while (cur <= weekEnd) {
      if (cur >= allocationStart && cur <= allocationEnd) {
        if (this.isAvailableDay(cur, holidays, timeOffDates)) {
          days.push(this.toDateOnlyString(cur))
        }
      }
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return days
  }

  private getWorkingDaysInMonth(year: number, month: number, allocationStart: Date, allocationEnd: Date, holidays: Holiday[], timeOffDates: Set<string>): string[] {
    const days: string[] = []
    const monthStart = new Date(Date.UTC(year, month, 1))
    const monthEnd = new Date(Date.UTC(year, month + 1, 0))

    const cur = new Date(monthStart.getTime())
    while (cur <= monthEnd) {
      if (cur >= allocationStart && cur <= allocationEnd) {
        if (this.isAvailableDay(cur, holidays, timeOffDates)) {
          days.push(this.toDateOnlyString(cur))
        }
      }
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return days
  }

  calculateDailyHoursFromAllocation(
    allocationStart: string,
    allocationEnd: string,
    hours: number,
    interval: AllocationInterval,
    holidays: Holiday[],
    timeOffDates: Set<string> = EMPTY_TIME_OFF
  ): DailyHoursMap {
    const numericHours = Number(hours) || 0
    const start = this.parseDateOnly(allocationStart)
    const end = this.parseDateOnly(allocationEnd)
    const dailyHours: DailyHoursMap = {}

    if (interval === 'Daily') {
      const cur = new Date(start.getTime())
      while (cur <= end) {
        if (this.isAvailableDay(cur, holidays, timeOffDates)) {
          dailyHours[this.toDateOnlyString(cur)] = numericHours
        }
        cur.setUTCDate(cur.getUTCDate() + 1)
      }
      return dailyHours
    }

    if (interval === 'Weekly') {
      const weeklyData: { [weekKey: string]: string[] } = {}

      const cur = new Date(start.getTime())
      while (cur <= end) {
        if (this.isAvailableDay(cur, holidays, timeOffDates)) {
          const weekKey = this.getWeekKey(cur)
          if (!weeklyData[weekKey]) weeklyData[weekKey] = []
          weeklyData[weekKey].push(this.toDateOnlyString(cur))
        }
        cur.setUTCDate(cur.getUTCDate() + 1)
      }

      Object.values(weeklyData).forEach(daysInWeek => {
        const hoursPerDay = numericHours / daysInWeek.length
        daysInWeek.forEach(day => { dailyHours[day] = hoursPerDay })
      })

      return dailyHours
    }

    if (interval === 'Monthly') {
      const monthlyData: { [monthKey: string]: string[] } = {}

      const cur = new Date(start.getTime())
      while (cur <= end) {
        if (this.isAvailableDay(cur, holidays, timeOffDates)) {
          const monthKey = this.getMonthKey(cur)
          if (!monthlyData[monthKey]) monthlyData[monthKey] = []
          monthlyData[monthKey].push(this.toDateOnlyString(cur))
        }
        cur.setUTCDate(cur.getUTCDate() + 1)
      }

      Object.values(monthlyData).forEach(daysInMonth => {
        const hoursPerDay = numericHours / daysInMonth.length
        daysInMonth.forEach(day => { dailyHours[day] = hoursPerDay })
      })

      return dailyHours
    }

    return dailyHours
  }

  calculateTotalHours(
    allocationStart: string,
    allocationEnd: string,
    hours: number,
    interval: AllocationInterval,
    holidays: Holiday[],
    timeOffDates: Set<string> = EMPTY_TIME_OFF
  ): number {
    const numericHours = Number(hours) || 0
    if (interval === 'Daily') {
      return dateCalculationService.getAvailableWorkingDays(allocationStart, allocationEnd, holidays, timeOffDates) * numericHours
    }

    if (interval === 'Weekly') {
      const dailyHours = this.calculateDailyHoursFromAllocation(allocationStart, allocationEnd, numericHours, interval, holidays, timeOffDates)
      const weeks = new Set<string>()
      Object.keys(dailyHours).forEach(date => weeks.add(this.getWeekKey(this.parseDateOnly(date))))
      return numericHours * weeks.size
    }

    if (interval === 'Monthly') {
      const dailyHours = this.calculateDailyHoursFromAllocation(allocationStart, allocationEnd, numericHours, interval, holidays, timeOffDates)
      const months = new Set<string>()
      Object.keys(dailyHours).forEach(date => months.add(this.getMonthKey(this.parseDateOnly(date))))
      return numericHours * months.size
    }

    return 0
  }

  getHoursForDateRange(
    allocationStart: string,
    allocationEnd: string,
    hours: number,
    interval: AllocationInterval,
    rangeStart: string,
    rangeEnd: string,
    holidays: Holiday[],
    timeOffDates: Set<string> = EMPTY_TIME_OFF
  ): number {
    const dailyHours = this.calculateDailyHoursFromAllocation(allocationStart, allocationEnd, hours, interval, holidays, timeOffDates)

    const start = this.parseDateOnly(rangeStart)
    const end = this.parseDateOnly(rangeEnd)

    let total = 0
    const cur = new Date(start.getTime())
    while (cur <= end) {
      const dateStr = this.toDateOnlyString(cur)
      if (dailyHours[dateStr]) {
        total += dailyHours[dateStr]
      }
      cur.setUTCDate(cur.getUTCDate() + 1)
    }

    return total
  }

  getNumberOfWeeks(startDate: string, endDate: string, holidays: Holiday[], timeOffDates: Set<string> = EMPTY_TIME_OFF): number {
    const dailyHours = this.calculateDailyHoursFromAllocation(startDate, endDate, 1, 'Weekly', holidays, timeOffDates)
    const weeks = new Set<string>()
    Object.keys(dailyHours).forEach(date => weeks.add(this.getWeekKey(this.parseDateOnly(date))))
    return weeks.size
  }

  getNumberOfMonths(startDate: string, endDate: string, holidays: Holiday[], timeOffDates: Set<string> = EMPTY_TIME_OFF): number {
    const dailyHours = this.calculateDailyHoursFromAllocation(startDate, endDate, 1, 'Monthly', holidays, timeOffDates)
    const months = new Set<string>()
    Object.keys(dailyHours).forEach(date => months.add(this.getMonthKey(this.parseDateOnly(date))))
    return months.size
  }
}

export const allocationDistributionService = new AllocationDistributionService()
